import { useState, useEffect, useRef } from "react";
import "./EarningsCalculator.css";

// ─── MATH MODEL (refined June 2026) ───────────────────────────────────────────
// peakNightly: realistic peak (Jun–Sep) nightly rate for a good-standard 1-bed
// peakOcc: realistic peak occupancy (share of nights sold)
// offRatio: off-season month gross / peak month gross (captures both lower rate + lower occupancy)
// confidence: tight = narrow range, wide = wider range
// Annual = 4 peak months + 8 off months (honest, not "365 nights sold")

const CITY_DATA = {
  ksamil:  { peakNightly: 95, peakOcc: 0.85, offRatio: 0.18, confidence: "tight", region: "coast" },
  saranda: { peakNightly: 82, peakOcc: 0.85, offRatio: 0.22, confidence: "tight", region: "coast" },
  dhermi:  { peakNightly: 88, peakOcc: 0.82, offRatio: 0.20, confidence: "tight", region: "coast" },
  vlora:   { peakNightly: 72, peakOcc: 0.82, offRatio: 0.28, confidence: "tight", region: "coast" },
  durres:  { peakNightly: 58, peakOcc: 0.80, offRatio: 0.35, confidence: "tight", region: "coast" },
  tirana:  { peakNightly: 50, peakOcc: 0.78, offRatio: 0.85, confidence: "tight", region: "city"  },
  shkoder: { peakNightly: 38, peakOcc: 0.70, offRatio: 0.55, confidence: "wide",  region: "city"  },
  berat:   { peakNightly: 42, peakOcc: 0.70, offRatio: 0.45, confidence: "wide",  region: "heritage" },
  theth:   { peakNightly: 55, peakOcc: 0.72, offRatio: 0.30, confidence: "wide",  region: "mountain" },
  other:   { peakNightly: 35, peakOcc: 0.68, offRatio: 0.50, confidence: "wide",  region: "other" },
};

// Property type IS the only size multiplier — no separate bedroom question
const PROP_MULTI = {
  room:   0.45,
  studio: 0.68,
  apt1:   1.00,
  apt2:   1.45,
  apt3:   1.85,
  villa:  2.40,
};

// Location quality — sea view lives ONLY here, never double-counted in features
const SPOT_MULTI = {
  seafront:    1.25,
  seaview:     1.14,
  center:      1.08,
  residential: 0.88,
  nature:      0.95,
  notsure:     1.00,
};

const COND_MULTI = {
  simple:    0.80,
  good:      1.00,
  renovated: 1.16,
  luxury:    1.35,
  notsure:   1.00,
};

// Features add a small capped boost — total capped at 18%
const FEAT_VALUE = {
  ac:       0.03,
  parking:  0.04,
  pool:     0.09,
  wifi:     0.02,
  elevator: 0.03,
  balcony:  0.04,
  garden:   0.04,
};
const FEAT_CAP = 0.18;

const SPREAD = { tight: 0.18, wide: 0.28 };
const WA_NUM = "355694343590";

function roundTo50(n) { return Math.round(n / 50) * 50; }

function getPotentialLabel(peakHigh) {
  if (peakHigh < 800)  return { label: "Modest Potential",  color: "#8a7d6e" };
  if (peakHigh < 1600) return { label: "Good Potential",    color: "#c8956c" };
  if (peakHigh < 3000) return { label: "Strong Potential",  color: "#5fae7e" };
  return                      { label: "Premium Potential", color: "#7cc4dd" };
}

function getBestSeason(region) {
  if (region === "coast")    return "Strongest demand June – September.";
  if (region === "city")     return "Demand is balanced year-round.";
  if (region === "mountain") return "Strongest in July – August and winter holidays.";
  if (region === "heritage") return "Demand peaks in spring and summer.";
  return "Demand varies by season.";
}

function calculate({ city, spot, propType, condition, features }) {
  const data  = CITY_DATA[city]      || CITY_DATA.other;
  const propM = PROP_MULTI[propType]  || 1;
  const spotM = SPOT_MULTI[spot]      || 1;
  const condM = COND_MULTI[condition] || 1;

  let featBoost = 0;
  features.forEach(f => { featBoost += FEAT_VALUE[f] || 0; });
  featBoost = Math.min(featBoost, FEAT_CAP);

  // Peak month gross = nightly × occupancy × 30 nights, scaled by property/spot/condition/features
  const peakBase   = data.peakNightly * data.peakOcc * 30;
  const peakCenter = peakBase * propM * spotM * condM * (1 + featBoost);
  const offCenter  = peakCenter * data.offRatio;

  const spread = SPREAD[data.confidence];
  const extraSpread = (spot === "notsure" || condition === "notsure") ? 0.07 : 0;
  const totalSpread = spread + extraSpread;

  const peakLow  = roundTo50(peakCenter * (1 - totalSpread));
  const peakHigh = roundTo50(peakCenter * (1 + totalSpread));
  const offLow   = roundTo50(offCenter  * (1 - totalSpread));
  const offHigh  = roundTo50(offCenter  * (1 + totalSpread));

  const annualCenter = peakCenter * 4 + offCenter * 8;
  const annualLow    = roundTo50(annualCenter * (1 - totalSpread));
  const annualHigh   = roundTo50(annualCenter * (1 + totalSpread));

  return {
    peakLow, peakHigh, offLow, offHigh, annualLow, annualHigh,
    potential: getPotentialLabel(peakHigh),
    bestSeason: getBestSeason(data.region),
    // for bar visualization — relative fill of off vs peak
    offFillPct: Math.round((offCenter / peakCenter) * 100),
  };
}

// ─── ANIMATED NUMBER HOOK ─────────────────────────────────────────────────────
function useCountUp(target, active, duration = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const from = 0;
    function tick(now) {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round((from + (target - from) * eased) / 50) * 50);
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else setVal(target);
    }
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active, duration]);
  return val;
}

// ─── RESULT DISPLAY (separate so hooks run cleanly on mount) ──────────────────
function ResultView({ result, city, propType, onRestart }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 60); return () => clearTimeout(t); }, []);

  const peakLow   = useCountUp(result.peakLow,   show);
  const peakHigh  = useCountUp(result.peakHigh,  show);
  const offLow    = useCountUp(result.offLow,    show);
  const offHigh   = useCountUp(result.offHigh,   show);
  const annualLow  = useCountUp(result.annualLow,  show, 1100);
  const annualHigh = useCountUp(result.annualHigh, show, 1100);

  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [leadErr, setLeadErr]       = useState("");

  async function handleLeadSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { setLeadErr("Please enter your name and phone."); return; }
    setSubmitting(true); setLeadErr("");
    try {
      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, phone, email,
          message: `Calculator lead — ${city} — ${propType} — Peak: €${result.peakLow}–€${result.peakHigh}/mo — Annual: €${result.annualLow}–€${result.annualHigh}`,
        }),
      });
      setSubmitted(true);
    } catch {
      setLeadErr("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  }

  const waMsg = encodeURIComponent(
    `Hello BookingAlb! I used your calculator and my estimate is €${result.peakLow}–€${result.peakHigh}/month in peak season. I'd like a free property review. City: ${city}, Property: ${propType}.`
  );

  return (
    <div className="ec-result">
      <div className="ec-result-hero">
        <div className="ec-result-eyebrow">Your estimate is ready</div>
        <p className="ec-result-intro">Based on real short-term rental data from your area</p>

        {/* Monthly bars */}
        <div className="ec-bars">
          <div className="ec-bar-row">
            <div className="ec-bar-head">
              <span className="ec-bar-label">Peak month · Jun–Sep</span>
              <span className="ec-bar-val">€{peakLow.toLocaleString()} – €{peakHigh.toLocaleString()}</span>
            </div>
            <div className="ec-bar-track">
              <div className={`ec-bar-fill peak${show ? " go" : ""}`} style={{ width: show ? "100%" : "0%" }}></div>
            </div>
          </div>
          <div className="ec-bar-row">
            <div className="ec-bar-head">
              <span className="ec-bar-label">Off-season month · Oct–May</span>
              <span className="ec-bar-val off">€{offLow.toLocaleString()} – €{offHigh.toLocaleString()}</span>
            </div>
            <div className="ec-bar-track">
              <div className={`ec-bar-fill off${show ? " go" : ""}`} style={{ width: show ? `${result.offFillPct}%` : "0%" }}></div>
            </div>
          </div>
        </div>

        {/* Annual */}
        <div className="ec-annual">
          <span className="ec-annual-label">Estimated annual income</span>
          <span className="ec-annual-val">€{annualLow.toLocaleString()} – €{annualHigh.toLocaleString()}</span>
          <span className="ec-annual-mo">/year</span>
        </div>

        <div className="ec-result-meta">
          <div className="ec-meta-item">
            <span className="ec-meta-label">Property potential</span>
            <span className="ec-meta-val" style={{ color: result.potential.color }}>{result.potential.label}</span>
          </div>
          <div className="ec-meta-sep"></div>
          <div className="ec-meta-item">
            <span className="ec-meta-label">Best season</span>
            <span className="ec-meta-val">{result.bestSeason}</span>
          </div>
        </div>

        <div className="ec-disclaimer">
          This is a rough estimate. Real income depends on photos, listing quality, response time, pricing strategy, reviews, and competition. Well-managed properties often perform toward the upper end of this range.
        </div>
      </div>

      {/* Lead capture */}
      <div className="ec-cta-block">
        <div className="ec-cta-header">
          <div className="ec-cta-title">Want a real number?</div>
          <div className="ec-cta-sub">A free 15-minute review with our team gives you real comparables from your street, a true pricing strategy, and an honest yes or no on whether we'd manage your property.</div>
        </div>

        {!submitted ? (
          <form className="ec-lead-form" onSubmit={handleLeadSubmit}>
            <div className="ec-lead-row">
              <input className="ec-lead-input" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required />
              <input className="ec-lead-input" placeholder="Phone or WhatsApp" value={phone} onChange={e => setPhone(e.target.value)} required />
            </div>
            <input className="ec-lead-input" placeholder="Email (optional)" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            {leadErr && <div className="ec-lead-err">{leadErr}</div>}
            <div className="ec-lead-btns">
              <button type="submit" className="ec-btn-submit" disabled={submitting}>
                {submitting ? "Sending..." : "Request free review"}
              </button>
              <a href={`https://wa.me/${WA_NUM}?text=${waMsg}`} target="_blank" rel="noreferrer" className="ec-btn-wa">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send via WhatsApp
              </a>
            </div>
            <div className="ec-lead-note">Free, no commitment. We respond within 24 hours.</div>
          </form>
        ) : (
          <div className="ec-success">
            <div className="ec-success-icon">✓</div>
            <div className="ec-success-text">Thanks {name}. We'll contact you within 24 hours.</div>
          </div>
        )}
      </div>

      <button className="ec-restart" onClick={onRestart}>← Try a different property</button>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function EarningsCalculator() {
  const [step, setStep]       = useState(0);
  const [animOut, setAnimOut] = useState(false);
  const [result, setResult]   = useState(null);

  const [city, setCity]           = useState("");
  const [spot, setSpot]           = useState("");
  const [propType, setPropType]   = useState("");
  const [condition, setCondition] = useState("");
  const [features, setFeatures]   = useState([]);

  function go(n) {
    setAnimOut(true);
    setTimeout(() => { setStep(n); setAnimOut(false); }, 220);
  }

  function toggleFeature(f) {
    setFeatures(p => p.includes(f) ? p.filter(x => x !== f) : [...p, f]);
  }

  function showResult() {
    setResult(calculate({ city, spot, propType, condition, features }));
    setAnimOut(true);
    setTimeout(() => {
      setStep(3);
      setAnimOut(false);
      setTimeout(() => document.querySelector(".ec-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }, 220);
  }

  function restart() {
    setAnimOut(true);
    setTimeout(() => {
      setStep(0); setResult(null);
      setCity(""); setSpot(""); setPropType(""); setCondition(""); setFeatures([]);
      setAnimOut(false);
    }, 220);
  }

  const canStep0 = city && spot;
  const canStep1 = propType;
  const canStep2 = condition;

  return (
    <section className="ec-section">
      <div className="ec-container">

        <div className="ec-header">
          <div className="ec-pill">
            <span className="ec-dot"></span>
            Free Estimate · No registration needed
          </div>
          <h2 className="ec-title">
            How much could your property<br/>
            <em>earn in Albania?</em>
          </h2>
          <p className="ec-subtitle">
            A conservative estimate based on real short-term rental data from your area.<br/>
            Takes about one minute.
          </p>
        </div>

        <div className="ec-card">
          {step < 3 && (
            <div className="ec-progress">
              {["Location", "Property", "Quality"].map((s, i) => (
                <div key={i} className={`ec-prog${i === step ? " active" : ""}${i < step ? " done" : ""}`}>
                  <div className="ec-prog-dot">
                    {i < step
                      ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                      : i + 1}
                  </div>
                  <span className="ec-prog-label">{s}</span>
                </div>
              ))}
              <div className="ec-prog-track">
                <div className="ec-prog-fill" style={{ width: `${step * 50}%` }}></div>
              </div>
            </div>
          )}

          <div className={`ec-body${animOut ? " out" : " in"}`}>

            {step === 0 && (
              <div className="ec-step">
                <div className="ec-step-title">Where is your property located?</div>
                <div className="ec-field">
                  <div className="ec-label">City</div>
                  <div className="ec-chips">
                    {[
                      ["ksamil","Ksamil"], ["saranda","Sarandë"], ["vlora","Vlorë"],
                      ["dhermi","Dhërmiu"], ["durres","Durrës"], ["tirana","Tirana"],
                      ["shkoder","Shkodër"], ["berat","Berat"], ["theth","Theth"], ["other","Other"],
                    ].map(([v, l]) => (
                      <button key={v} className={`ec-chip${city === v ? " on" : ""}`} onClick={() => setCity(v)}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="ec-field">
                  <div className="ec-label">
                    What best describes the location?
                    <span className="ec-hint">If you're not sure, we widen the range to be honest.</span>
                  </div>
                  <div className="ec-options">
                    {[
                      ["seafront",    "🏖️", "Right on the seafront or beachfront"],
                      ["seaview",     "🌊", "Sea or lake view"],
                      ["center",      "🏙️", "City centre or main area"],
                      ["residential", "🌿", "Quiet residential area"],
                      ["nature",      "⛰️", "Mountain or nature area"],
                      ["notsure",     "🤔", "Not sure"],
                    ].map(([v, icon, l]) => (
                      <button key={v} className={`ec-opt${spot === v ? " on" : ""}`} onClick={() => setSpot(v)}>
                        <span className="ec-opt-icon">{icon}</span>
                        <span className="ec-opt-text">{l}</span>
                        {spot === v && <span className="ec-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ec-actions single">
                  <button className="ec-btn-next" disabled={!canStep0} onClick={() => go(1)}>
                    Continue
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="ec-step">
                <div className="ec-step-title">What type of property do you have?</div>
                <div className="ec-field">
                  <div className="ec-label">Property type</div>
                  <div className="ec-prop-grid">
                    {[
                      ["room",   "🛏️", "Room",         "Private room"],
                      ["studio", "🏠", "Studio",        "Open plan"],
                      ["apt1",   "🏢", "Apt 1+1",       "1 bedroom"],
                      ["apt2",   "🏡", "Apt 2+1",       "2 bedrooms"],
                      ["apt3",   "🏘️", "Apt 3+1",       "3 bedrooms"],
                      ["villa",  "🏖️", "Villa / House", "Large property"],
                    ].map(([v, icon, name, sub]) => (
                      <button key={v} className={`ec-prop${propType === v ? " on" : ""}`} onClick={() => setPropType(v)}>
                        <span className="ec-prop-icon">{icon}</span>
                        <span className="ec-prop-name">{name}</span>
                        <span className="ec-prop-sub">{sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ec-actions">
                  <button className="ec-btn-back" onClick={() => go(0)}>← Back</button>
                  <button className="ec-btn-next" disabled={!canStep1} onClick={() => go(2)}>
                    Continue
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="ec-step">
                <div className="ec-step-title">Condition and features</div>
                <div className="ec-field">
                  <div className="ec-label">What condition is the property in?</div>
                  <div className="ec-options">
                    {[
                      ["simple",    "Simple but usable",  "Functional and clean"],
                      ["good",      "Good standard",      "Well maintained"],
                      ["renovated", "Recently renovated", "Updated in last few years"],
                      ["luxury",    "Premium / luxury",   "High-end finish"],
                      ["notsure",   "Not sure",           "We'll widen the range to be fair"],
                    ].map(([v, l, sub]) => (
                      <button key={v} className={`ec-opt${condition === v ? " on" : ""}`} onClick={() => setCondition(v)}>
                        <div>
                          <span className="ec-opt-text">{l}</span>
                          <span className="ec-opt-sub">{sub}</span>
                        </div>
                        {condition === v && <span className="ec-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ec-field">
                  <div className="ec-label">
                    Does the property have any of these?
                    <span className="ec-optional">optional</span>
                  </div>
                  <div className="ec-feat-grid">
                    {[
                      ["ac",       "❄️", "Air conditioning"],
                      ["parking",  "🚗", "Parking"],
                      ["pool",     "🏊", "Pool"],
                      ["balcony",  "🌅", "Balcony or terrace"],
                      ["garden",   "🌿", "Garden"],
                      ["elevator", "🛗", "Elevator"],
                      ["wifi",     "📶", "Fast WiFi"],
                    ].map(([v, icon, l]) => (
                      <button key={v} className={`ec-feat${features.includes(v) ? " on" : ""}`} onClick={() => toggleFeature(v)}>
                        <span>{icon}</span>
                        <span>{l}</span>
                        {features.includes(v) && <span className="ec-feat-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ec-actions">
                  <button className="ec-btn-back" onClick={() => go(1)}>← Back</button>
                  <button className="ec-btn-primary" disabled={!canStep2} onClick={showResult}>
                    Show My Estimate
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
            )}

            {step === 3 && result && (
              <ResultView result={result} city={city} propType={propType} onRestart={restart} />
            )}

          </div>
        </div>
      </div>
    </section>
  );
}